/**
 * DashShell — the responsive dashboard chrome (phase 7 Track A).
 * Four-destination IA from design/project/lib/dash.jsx (phone TopBar +
 * TabBar) and dash-desktop.jsx (DSidebar + DTopBar): ONE app, the 900px
 * CSS breakpoint decides phone tab bar vs desktop sidebar — not a toggle.
 *
 * Tenant switcher + PushBell + sign-out live in the top bar / tenant
 * strip on phone and in the sidebar foot on desktop (both are rendered;
 * CSS shows one per the contract).
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { SwarmMoon, Icon, I, Btn, MonoChip } from "../kit";
import { planLabel } from "../lib/plan";
import { TenantSwitcher } from "./TenantSwitcher";
import { PushBell } from "./PushBell";
import { SiteFooter } from "./SiteFooter";

export type DashTab = "tonight" | "swarm" | "schedules" | "activity";

export const TAB_PATH: Record<DashTab, string> = {
  tonight: "/dashboard",
  swarm: "/dashboard/swarm",
  schedules: "/dashboard/schedules",
  activity: "/dashboard/activity",
};

const TABS: { k: DashTab; label: string; icon: string }[] = [
  { k: "tonight", label: "Tonight", icon: I.moon },
  { k: "swarm", label: "Swarm", icon: I.cpu },
  { k: "schedules", label: "Schedules", icon: I.clock },
  { k: "activity", label: "Activity", icon: I.signal },
];

/** Desktop sidebar groups (dash-desktop D_NAV, folded to the 4-tab IA). */
const NAV_GROUPS: { group: string; items: DashTab[] }[] = [
  { group: "Watch", items: ["tonight"] },
  { group: "Swarm", items: ["swarm"] },
  { group: "Work", items: ["schedules", "activity"] },
];

export function DashShell({
  active,
  crumb,
  userEmail,
  entitlements,
  onSignOut,
  onNewSchedule,
  children,
}: {
  /** Highlighted destination — null for detail pages outside the four. */
  active: DashTab | null;
  /** Desktop breadcrumb tail ("tonight", "machine", …). */
  crumb: string;
  userEmail?: string;
  /** /auth/me ownedEntitlements — drives the plan chip (phase 10 §C).
   *  Per-USER, not per-tenant: plan gates always read the caller's
   *  entitlements regardless of the selected tenant (on the plane, the
   *  "ownerEmail" table attribute holds a tenantId — personal = the
   *  user's email, team = teamId uuid — but entitlements live on the
   *  UsersTable row keyed by email). */
  entitlements?: string[];
  onSignOut: () => void;
  /** Renders the desktop "+ New schedule" action when provided. */
  onNewSchedule?: () => void;
  children: ReactNode;
}) {
  const initial = (userEmail ?? "?").slice(0, 1);
  const plan = planLabel(entitlements);
  return (
    <div className="dsh-app">
      {/* desktop sidebar (≥900px) */}
      <aside className="dsh-side">
        <Link to="/dashboard" className="dsh-side-brand">
          <SwarmMoon size={22} />
          <span className="dsh-wordmark">nightswarm</span>
        </Link>
        <nav className="dsh-nav">
          {NAV_GROUPS.map((g) => (
            <div key={g.group}>
              <div className="dsh-nav-label">{g.group}</div>
              <div className="dsh-nav-items">
                {g.items.map((k) => {
                  const t = TABS.find((x) => x.k === k)!;
                  return (
                    <Link
                      key={k}
                      to={TAB_PATH[k]}
                      className={`dsh-nav-item${active === k ? " active" : ""}`}
                    >
                      <Icon d={t.icon} size={16} />
                      {t.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="dsh-side-foot">
          <TenantSwitcher />
          <Link to="/docs" className="dsh-docs-link mono">
            <Icon d={I.list} size={13} /> Docs
          </Link>
          <div className="dsh-bell-row">
            <PushBell />
            <span
              className="mono"
              style={{ fontSize: 10.5, color: "var(--color-fg-4)" }}
            >
              push alerts
            </span>
          </div>
          {/* plan chip (phase 10 §C) — free/pro/lifetime/teams → /pricing */}
          {entitlements && (
            <Link
              to="/pricing"
              className="dsh-plan-chip"
              title="Plan & pricing"
            >
              <MonoChip tone={plan === "free" ? "ghost" : "accent"}>
                plan · {plan}
              </MonoChip>
            </Link>
          )}
          <div className="dsh-user-card">
            <span className="dsh-avatar">{initial}</span>
            <span className="dsh-user-meta">
              <span className="dsh-user-email" title={userEmail}>
                {userEmail}
              </span>
            </span>
            <button
              type="button"
              className="dsh-user-signout"
              onClick={onSignOut}
            >
              sign out
            </button>
          </div>
        </div>
      </aside>

      <div className="dsh-body">
        <header className="dsh-top">
          {/* phone brand */}
          <Link to="/dashboard" className="dsh-top-brand">
            <SwarmMoon size={22} />
            <span className="dsh-wordmark">nightswarm</span>
          </Link>
          {/* desktop breadcrumb */}
          <span className="dsh-crumb">
            <span className="dim">nightswarm</span>
            <span className="sep">/</span>
            <span className="here">{crumb}</span>
          </span>
          <div className="dsh-top-actions">
            {onNewSchedule && (
              <Btn
                variant="primary"
                size="sm"
                className="dsh-newsched"
                onClick={onNewSchedule}
              >
                <Icon d={I.plus} size={14} /> New schedule
              </Btn>
            )}
            {/* phone: docs + bell + sign-out (desktop keeps them in the sidebar) */}
            <Link
              to="/docs"
              className="dsh-iconbtn"
              title="Docs"
              aria-label="Documentation"
            >
              <Icon d={I.list} size={14} />
            </Link>
            <PushBell />
            <button
              type="button"
              className="dsh-iconbtn"
              onClick={onSignOut}
              title="Sign out"
              aria-label="Sign out"
            >
              <Icon d={I.power} size={15} />
            </button>
          </div>
        </header>

        {/* phone tenant strip (<900px) — switcher needs its own row; the
            plan chip rides here since the user card is desktop-only */}
        <div className="dsh-tenantbar">
          <TenantSwitcher />
          {entitlements && (
            <Link
              to="/pricing"
              className="dsh-plan-chip"
              title="Plan & pricing"
            >
              <MonoChip tone={plan === "free" ? "ghost" : "accent"}>
                plan · {plan}
              </MonoChip>
            </Link>
          )}
        </div>

        <main className="dsh-screen">
          {children}
          <SiteFooter />
        </main>

        {/* phone bottom tab bar (<900px) */}
        <nav className="dsh-tabbar">
          {TABS.map((t) => (
            <Link
              key={t.k}
              to={TAB_PATH[t.k]}
              className={`dsh-tab${active === t.k ? " active" : ""}`}
            >
              <Icon d={t.icon} size={20} />
              <span>{t.label}</span>
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
}

/** Uppercase mono section label with an optional right-side action. */
export function SecLabel({
  children,
  action,
}: {
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="dsh-seclabel">
      <span>{children}</span>
      {action}
    </div>
  );
}

/** Teaching empty state (dash Dashboard.html EmptyTab). */
export function EmptyState({
  icon,
  title,
  body,
  cta,
}: {
  icon: string;
  title: string;
  body: ReactNode;
  cta?: ReactNode;
}) {
  return (
    <div className="dsh-empty">
      <span className="dsh-empty-icon">
        <Icon d={icon} size={26} />
      </span>
      <h3>{title}</h3>
      <p>{body}</p>
      {cta}
    </div>
  );
}
